import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import AdvanceGridHeader from './AdvanceGridHeader';
import { getDD, getListByDD } from 'y4/Y4Api';
import { getQueries } from 'y4/Utils';
import FilterPanel from '../filter/FilterPanel';
import StatisticsPanel from './StatisticsPanel';
import y, { textAlignToClass, formatByNamedArgs, removeUi } from 'y4/Utils';
import { run as runCommand } from '../Executer';
import _ from 'lodash';
import ActionButton from 'components/common/ActionButton';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/** Load all data from back end for DataPanel
 * @param {*} panel Data panel of Y4
 * @param {*} withDd false: Without Data defination
 * @param {*} queries
 * @returns
 */
async function loadData(panel, withDd = true, queries = {}) {
  console.log('Load data for panel', queries);
  const dataCode = panel.dataCode;

  //Get data definition
  let rt_dd = null;
  if (withDd) {
    rt_dd = await getDD(dataCode, queries);
    if (rt_dd.isNotOk()) return { rt: rt_dd };
  }

  //Get all list and statistics by data definitioin
  const rt_data = await getListByDD(dataCode, {
    count: -1,
    statistics: 'true',
    ...queries
  });
  if (rt_data.isNotOk()) return { rt: rt_data };

  //Return async data
  return {
    rt: rt_data, //last rt
    filters: rt_dd && rt_dd.data.queries,
    fields: rt_dd && rt_dd.data.fields,
    rows: rt_data.data.list, //rows
    statistics: rt_data.data.statistics,
    selectedIds: rt_data.data.selectedIds
  };
}

/**
 * By fields from data definition and panel definition
 * to generate columns from React-Table
 * @param {*} panel
 * @param {*} fields
 * @returns
 */
const generateColumns = (panel, fields) => {
  const buttonClick = (e, rows) => {
    if (panel.buttonClick) panel.buttonClick(e, rows);
  };

  //Generate columns form React-Table7
  let columns = fields
    .filter(field => !field.hidden)
    .map(f => {
      //columns that do some convert
      const c = {
        id: f.name,
        //accessor: f.name + 'Ui', //f.name has '.' can't show values
        accessor: originalRow => {
          return originalRow[f.name + 'Ui'];
        },
        Header: f.title,
        cellProps: {
          className: textAlignToClass(f.textAlign)
        },
        headerProps: {
          className: textAlignToClass(f.captionAlign)
        }
      };
      if (f.type == 'Boolean') {
        c.Cell = rowData => {
          const checked = rowData.row.original[f.name + 'Ui'];
          return (
            <input
              className="form-check-input"
              type="checkbox"
              disabled
              checked={checked}
            />
          );
        };
      } else if (f.type == 'Text') {
        if (_.get(f, 'other.extraType') == 'Html') {
          c.Cell = rowData => {
            const value = rowData.row.original[f.name + 'Ui'];
            return (
              <div
                dangerouslySetInnerHTML={{
                  __html: value ?? ''
                }}
              ></div>
            );
          };
        }
      }
      return c;
    });

  //Add action columns
  if (!_.isEmpty(panel.buttons)) {
    columns = _.concat(columns, {
      accessor: 'none',
      Header: '',
      disableSortBy: true,
      with: 50,
      cellProps: {
        className: 'text-end py-2'
      },
      Cell: rowData => {
        const row_data = removeUi(rowData.data[rowData.row.index]);
        return (
          <>
            {panel.buttons
              .filter(item => {
                return item.type == 'row';
              })
              .map((button, index) => {
                const button_visible =
                  _.isEmpty(button.condition) ||
                  eval(
                    formatByNamedArgs(button.condition, k => {
                      return row_data[k];
                    })
                  );
                return button_visible ? (
                  <ActionButton
                    key={index}
                    icon={button.icon}
                    title={button.text}
                    onClick={() => buttonClick(button, [row_data])}
                    variant="action"
                    className="p-0 me-2"
                  />
                ) : null;
              })}
          </>
        );
      }
    });
  }
  return columns;
};

/**
 *
 * @param {*} panel
 * @returns
 */
const AdvanceGrid = ({ form, panel }) => {
  const { t } = useTranslation('data');

  const navigate = useNavigate();

  const location = useLocation();
  const queries = getQueries(location.search);

  const maintaining = _.get(panel, 'option.maintaining', false);
  const cache_key = y.md5(`${form.code}_${panel.code}_data`) + '_d';

  //Data to show by state
  const [data, setData] = useState({
    error: 1,
    errorTitle: t('Loading....'),
    errorDescription: t('Data is still loading, please wait')
  });

  const [embedBadges, setEmbedBadges] = useState();

  //Load data after UI shown
  useEffect(() => {
    const cached = y.getSessionItem(cache_key);
    if (maintaining && cached) {
      const cached_object = JSON.parse(cached);
      panel.currentFilters = cached_object.filterValues;
      panel.loadedFields = cached_object.fields; //Columns info in DataDefinition
      panel.loadedFilters = cached_object.filters; //Filters info in DataDefinition
      setDataAndPanelLoaded({
        columns: generateColumns(panel, cached_object.fields),
        rows: cached_object.rows,
        statistics: cached_object.statistics,
        selectedIds: cached_object.selectedIds,
        filters: cached_object.filters
      });
    } else {
      //Default filters
      const dfs = _.get(panel, 'option.defaultFilters', {});
      setFilter(dfs);
      const all_filter = { ...dfs, ...queries };
      //Load data from back end
      loadData(panel, true, all_filter).then(
        ({ rt, filters, fields, rows, statistics, selectedIds }) => {
          if (rt.isOk()) {
            panel.currentFilters = all_filter;
            panel.loadedFields = fields;
            panel.loadedFilters = filters;
            setDataAndPanelLoaded({
              columns: generateColumns(panel, fields),
              rows,
              statistics,
              selectedIds,
              filters
            });
            //First time save info to session
            if (maintaining) {
              y.setSessionItem(
                cache_key,
                JSON.stringify({
                  filterValues: all_filter,
                  filters,
                  fields,
                  rows,
                  statistics,
                  selectedIds
                })
              );
            }
          } else {
            setData({
              error: 1,
              errorTitle: `Error ${rt.id}` + (rt.code ? `(${rt.code})` : ''),
              errorDescription: rt.message
            });
          }
        }
      );
    }
  }, []);

  //Search data
  const [filter, setFilter] = useState();
  const search = fs => {
    setFilter(fs);
    const all_filter = { ...fs, ...queries };
    return loadData(panel, false, all_filter).then(
      ({ rt, rows, statistics, selectedIds }) => {
        if (rt.isOk()) {
          panel.currentFilters = fs;
          setDataAndDispatch({
            columns: generateColumns(panel, panel.loadedFields),
            filters: panel.loadedFilters, //filter items info
            rows,
            statistics,
            selectedIds
          });

          //Save info to session when search
          if (maintaining) {
            y.setSessionItem(
              cache_key,
              JSON.stringify({
                filterValues: fs,
                filters: panel.loadedFilters,
                fields: panel.loadedFields,
                rows,
                statistics,
                selectedIds
              })
            );
          }
        } else {
          setData({
            error: 1,
            errorTitle: `Error ${rt.id}` + (rt.code ? `(${rt.code})` : ''),
            errorDescription: rt.message
          });
        }
      }
    );
  };

  //Buttons click
  const buttonClick = (button, rows) => {
    return runCommand({
      form,
      panel,
      command: button.command,
      params: button.params,
      javascript: button.javascript,
      dataCode: panel.dataCode,
      successMessage: button.successMessage,
      successCommand: button.successCommand,
      navigate: navigate,
      rows: rows,
      filter: filter,
      queries: queries
    });
  };

  //Row button click to use this function
  panel.buttonClick = buttonClick;

  //Dispatch afterPanelLoaded;
  const dispatchAfterPanelLoaded = d => {
    form.eventTarget.dispatchEvent(
      new CustomEvent('form-event', {
        detail: {
          event: 'afterPanelLoaded',
          panel,
          form,
          data: d
        }
      })
    );
  };
  //Set data and dispatch afterDataLoaded
  const setDataAndDispatch = d => {
    setData(d);
    form.eventTarget.dispatchEvent(
      new CustomEvent('form-event', {
        detail: {
          event: 'afterDataLoaded',
          panel,
          form,
          data: d
        }
      })
    );
  };

  //Set data and dispatch afterPanelLoaded and afterDataLoaded
  const setDataAndPanelLoaded = d => {
    setDataAndDispatch(d);
    dispatchAfterPanelLoaded(d);
  };

  //Dispatch selectionChanged
  const selectionChanged = d => {
    form.eventTarget.dispatchEvent(
      new CustomEvent('form-event', {
        detail: {
          event: 'selectionChanged',
          panel,
          form,
          data: d
        }
      })
    );
  };

  //Listen events
  useEffect(() => {
    form.eventTarget.addEventListener('form-event', e => {
      const event = e.detail.event;
      const path = 'events.listen' + y.firstCharToUpper(event);
      const script = y.gpv(panel, path);
      if (script) {
        const f = Function('y', 'source', 'target', script);
        f(y, e.detail, {
          form,
          panel,
          data,
          setData,
          setDataAndDispatch,
          search,
          embedBadges,
          setEmbedBadges
        });
      }
    });
  }, [form.eventTarget]);

  panel.currentRows = data.rows;

  return (
    data &&
    (data.error ? (
      <>
        <h5 className="mb-0 mt-1">{data.errorTitle}</h5>
        <div className="pt-3">{data.errorDescription}</div>
      </>
    ) : (
      <>
        <FilterPanel
          cacheId={
            maintaining ? y.md5(`${form.code}_${panel.code}_filter`) : null
          }
          panel={panel}
          filters={data.filters} //filter fields
          values={filter} //filter values
          search={search}
        ></FilterPanel>
        <AdvanceTableWrapper
          cacheId={
            maintaining ? y.md5(`${form.code}_${panel.code}_grid`) : null
          }
          columns={data.columns}
          data={data.rows}
          selectedIds={data.selectedIds}
          sortable
          pagination
          selectionChanged={selectionChanged}
          perPage={10}
          selection={panel.option && panel.option.multiSelect}
        >
          <AdvanceGridHeader
            table
            buttons={panel.buttons}
            buttonExecuter={buttonClick}
            badges={embedBadges}
          />
          <AdvanceTable
            table
            headerClassName="bg-200 text-900 text-nowrap align-middle"
            rowClassName="align-middle white-space-nowrap"
            tableProps={{
              bordered: true,
              striped: true,
              className: 'fs--1 mb-0 overflow-hidden'
            }}
          />
          <div className="mt-3">
            <AdvanceTableFooter
              rowCount={data.rows.length}
              table
              rowInfo
              navButtons
              rowsPerPageSelection
            />
          </div>
        </AdvanceTableWrapper>
        <StatisticsPanel statistics={data.statistics}></StatisticsPanel>
      </>
    ))
  );
};

AdvanceGrid.propTypes = {
  form: PropTypes.object,
  panel: PropTypes.object
};

export default AdvanceGrid;
